<template>
  <div class="timeblock__header">
    <div class="timeblock__header__top">
      <p>
        Časový blok {{ index + 1 }}
        -
        {{ employee?._fullName }}
      </p>
    </div>

    <div class="timeblock__header__content">
      <div class="timeblock__date-wrapper">
        <div class="timeblock__date">
          <button
            class="timeblock__date-button"
            :disabled="isCurrentDay || !isEditable"
            @click="$emit('previousDay')"
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
              <path d="M15.41 7.41L14 6l-6 6 6 6 1.41-1.41L10.83 12z" />
            </svg>
          </button>

          <Z-datepicker
            :key="dateFormat"
            :model-value="modelValue.date"
            :format="dateFormat"
            locale="sk"
            select-text="Vybrať"
            cancel-text="Zrušiť"
            placeholder="Dátum"
            :auto-apply="true"
            :enable-time-picker="false"
            :min-date="minDate"
            :disabled="!isEditable"
            class="timeblock__date-picker"
            :class="{ 'timeblock__date-picker--disabled': !isEditable }"
            @update:model-value="$emit('updateDate', $event)"
          />

          <button
            class="timeblock__date-button"
            :disabled="!isEditable"
            @click="$emit('nextDay')"
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
              <path d="M8.59 16.59L10 18l6-6-6-6-1.41 1.41L13.17 12z" />
            </svg>
          </button>
        </div>

        <div class="actions actions--mobile">
          <a-employee-timeblock-header-actions
            :deletable="deletable"
            @add-timeblock="$emit('addTimeblock')"
            @remove-timeblock="$emit('removeTimeblock')"
          />
        </div>
      </div>

      <div class="timeblock__controls">
        <div class="timeblock__controls__time">
          <v-text-field
            :value="modelValue.starts_at_time"
            :class="{
              'error-input':
                collision.startCollides ||
                !isTimeRangeValid ||
                (hasValidationErrors && hasStartTimeError),
            }"
            :disabled="!isEditable"
            type="time"
            variant="solo"
            bg-color="white"
            label="Začiatok"
            density="compact"
            hide-details
            @input="$emit('updateStartTime', $event)"
          />
          <v-text-field
            :value="modelValue.ends_at_time"
            :class="{
              'error-input':
                collision.endCollides ||
                !isTimeRangeValid ||
                (hasValidationErrors && hasEndTimeError),
            }"
            :disabled="!isEditable"
            type="time"
            variant="solo"
            bg-color="white"
            label="Koniec"
            density="compact"
            hide-details
            @input="$emit('updateEndTime', $event)"
          />
        </div>

        <div class="timeblock__time-overview">
          <p>
            <span
              class="timeblock__time-overview__label timeblock__time-overview__label--desktop"
              >Naplánovaný čas:</span
            >
            <span
              class="timeblock__time-overview__label timeblock__time-overview__label--mobile"
              >Nap. čas:</span
            >
            <span>{{ plannedTime }}h</span>
          </p>
          <p>
            <span
              class="timeblock__time-overview__label timeblock__time-overview__label--desktop"
              >Volný čas:</span
            >
            <span
              class="timeblock__time-overview__label timeblock__time-overview__label--mobile"
              >Vol. čas:</span
            >
            <span :style="{ color: Number(freeTime) < 0 ? 'red' : undefined }"
              >{{ freeTime }}h</span
            >
          </p>
        </div>
      </div>
      <div
        v-if="!isTimeRangeValid || endTimeErrorMessage"
        class="validation-error"
      >
        {{ endTimeErrorMessage || 'Koniec musí byť po začiatku' }}
      </div>

      <div class="actions actions--desktop">
        <a-employee-timeblock-header-actions
          :deletable="deletable"
          @add-timeblock="$emit('addTimeblock')"
          @remove-timeblock="$emit('removeTimeblock')"
        />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref } from 'vue';
import moment from 'moment';
import ZDatepicker from '@/plugins/tm@_components/z-datepicker.vue';
import type { EmployeeFullData, TimeblockData } from './types';
import AEmployeeTimeblockHeaderActions from './a-employee-timeblock-header-actions.vue';

interface Props {
  modelValue: TimeblockData;
  minDate: Date;
  deletable: boolean;
  isEditable: boolean;
  isTimeRangeValid: boolean;
  employee: EmployeeFullData;
  index: number;
  collision: {
    hasCollision: boolean;
    startCollides: boolean;
    endCollides: boolean;
  };
  hasValidationErrors: boolean;
  hasStartTimeError: boolean;
  hasEndTimeError: boolean;
  endTimeErrorMessage?: string;
  plannedTime: string | number;
  freeTime: string | number;
}

const props = withDefaults(defineProps<Props>(), {
  endTimeErrorMessage: '',
  plannedTime: '',
  freeTime: '',
});

defineEmits<{
  (e: 'previousDay'): void;
  (e: 'nextDay'): void;
  (e: 'updateDate', value: Date | null): void;
  (e: 'updateStartTime', event: Event): void;
  (e: 'updateEndTime', event: Event): void;
  (e: 'addTimeblock'): void;
  (e: 'removeTimeblock'): void;
}>();

// Check if selected date is today (disable previous day button if true)
const isCurrentDay = computed(() => {
  if (!props.modelValue.date) return false;
  return moment(props.modelValue.date).isSame(moment(), 'day');
});

const isMobile = ref(false);

const handleResize = () => {
  isMobile.value = window.innerWidth <= 768;
};

onMounted(() => {
  handleResize();
  window.addEventListener('resize', handleResize);
});

onUnmounted(() => {
  window.removeEventListener('resize', handleResize);
});

const dateFormat = computed(() => {
  return isMobile.value ? 'dd.MM' : 'dd.MM.yyyy';
});
</script>

<style lang="sass" scoped>
.timeblock__header
  container-type: inline-size
  container-name: timeblock-header

.timeblock__header__top
  padding: 8px 16px 0 16px
  font-weight: 400
  font-size: 14px

.timeblock__header__content
  display: flex
  align-items: center
  gap: 15px
  flex-wrap: wrap
  border-bottom: 1px solid #e5e5e5
  padding: 8px 16px

  @container timeblock-header (max-width: 768px)
    flex-direction: column
    align-items: stretch

.timeblock__date-wrapper
  display: flex
  align-items: center
  justify-content: space-between

  @container timeblock-header (max-width: 768px)
    width: 100%

.timeblock__date
  display: flex
  align-items: center
  flex: 1
  max-width: 220px

  @container timeblock-header (max-width: 768px)
    max-width: 170px

    ::v-deep
      .dp__input
        padding: 0 0 0 29px !important
      .dp__input_icon
        padding: 6px 9px !important
        left: 0 !important

.timeblock__date-button
  background: #f5f5f5
  border: 1px solid #e0e0e0
  border-radius: 4px
  width: 44px
  height: 44px
  display: flex
  align-items: center
  justify-content: center
  cursor: pointer
  transition: all 0.2s

  &:hover:not(:disabled)
    background: #e0e0e0

  &:active:not(:disabled)
    background: #d0d0d0

  &:disabled
    opacity: 0.4
    cursor: not-allowed

  &:first-child
    border-top-right-radius: 0
    border-bottom-right-radius: 0
    border-right: none

  &:last-child
    border-top-left-radius: 0
    border-bottom-left-radius: 0
    border-left: none

.timeblock__date-picker
  flex: 1
  border-radius: 0

  ::v-deep
    .dp__input
      border-radius: 0
      height: 44px

    .dp__clear_icon
      display: none

  &.has-value ::v-deep
    .dp__input
      padding-top: 13px !important
      padding-bottom: 11px !important
    .dp__icon
      top: 23px !important

  &--disabled
    opacity: 0.5
    cursor: not-allowed !important

    ::v-deep
      .dp__input_wrap
        cursor: not-allowed !important

      .dp__input
        cursor: not-allowed !important

.timeblock__controls
  display: flex
  gap: 10px
  flex: 1

  &__time
    display: flex
    gap: 10px

  @container timeblock-header (max-width: 768px)
    width: 100%
    flex: 1

//   ::v-deep .v-input
//     width: 140px !important
//     flex-grow: 0

//   @media (max-width: 768px)
//     ::v-deep
//       .v-input
//         width: 58px !important
//       .v-label
//         margin-inline-start: 5px !important
//       .v-field__input
//         padding-inline-start: 5px !important
//         padding-inline-end: 5px !important

.timeblock__time-overview
  height: 44px
  display: flex
  flex-direction: column
  justify-content: space-between
  flex-shrink: 0
  min-width: max-content

  p
    font-size: 14px
    font-weight: 400
    white-space: nowrap
    display: flex
    gap: 4px

    span:not(.timeblock__time-overview__label)
      font-weight: 500
      color: #7f39fb
      white-space: nowrap
      flex-shrink: 0

.error-input
  ::v-deep input
    border-bottom: 2px solid #ff5252 !important

.validation-error
  color: #ff5252
  font-size: 12px
  margin-top: 5px
  padding-left: 10px

.actions--mobile
  display: none
  @container timeblock-header (max-width: 768px)
    display: flex

.actions--desktop
  display: block

  @container timeblock-header (max-width: 768px)
    display: none

.timeblock__time-overview__label--desktop
  display: block

  @container timeblock-header (max-width: 768px)
    display: none

.timeblock__time-overview__label--mobile
  display: none

  @container timeblock-header (max-width: 768px)
    display: block
    white-space: nowrap
</style>
